import { jsx as _jsx } from "react/jsx-runtime";
import * as d3 from "d3";
import { useEffect, useRef, useState } from "react";
import { createAxes, drawAxes } from "../../utils/axesUtils";
import { drawLegend } from "../../utils/legendUtils";
import { createScales } from "../../utils/scalesUtils";
import { throttle } from "../../utils/throttle";
import { drawGridlines, drawLinesAndAreas } from "./drawUtils";
import { defineGradients } from "./gradientsUtils";
export function LineChart({ datasets = [], timeWindow = 60, yMin = 0, yMax = 100, yUnits = "", xTicks = 4, yTicks = 4, xMajorTickInterval = 2, yMajorTickInterval = 2, padding = {
    x: 0,
    y: 0
}, beg, containerClass, end, xFormat, logarithmic = false }) {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const [dimensions, setDimensions] = useState({
        width: 0,
        height: 0
    });
    const tooltipRef = useRef(null);
    const focusLineRef = useRef(null);
    const lastMouseXRef = useRef(null);
    const overlayRectRef = useRef(null);
    const randomId = ()=>Math.random().toString(36).slice(2, 9);
    const clipPathId = useRef(`clip-${randomId()}`).current;
    const gradientIds = useRef(datasets.map((_, i)=>`gradient-${i}-${randomId()}`)).current;
    useEffect(()=>{
        if (!containerRef.current) return;
        // On mount, create tooltip in parent <div>
        tooltipRef.current = d3.select(containerRef.current).append("div").attr("class", "tooltip").style("position", "absolute").style("opacity", 0).style("background-color", "white").style("border", "solid 1px").style("border-radius", "5px").style("padding", "15px").style("transition", "opacity 0.2s ease-in-out").style("pointer-events", "none");
        return ()=>{
            // Cleanup if unmount
            tooltipRef.current?.remove();
            tooltipRef.current = null;
        };
    }, []);
    useEffect(()=>{
        const handleResize = ()=>{
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({
                    width,
                    height
                });
            }
        };
        handleResize(); // Initial call to set dimensions
        window.addEventListener("resize", handleResize); // Update on window resize
        return ()=>window.removeEventListener("resize", handleResize);
    }, []);
    useEffect(()=>{
        if (dimensions.width === 0 || dimensions.height === 0) return;
        const width = dimensions.width;
        const height = dimensions.height;
        const legendHeight = datasets.length > 0 ? 30 : 0;
        const margin = {
            top: 10,
            right: 35,
            bottom: 25 + padding["y"] + legendHeight,
            left: 55 + padding["x"]
        };
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;
        // Create scales
        const now = end ?? new Date();
        const beginning = beg ?? new Date(now.getTime() - timeWindow * 60 * 1000);
        const scales = createScales(beginning, now, yMin, yMax, innerWidth, innerHeight, logarithmic);
        // Create axes
        const axes = createAxes(scales.xScale, scales.yScale, beginning, now, xTicks, yTicks, xMajorTickInterval, yMajorTickInterval, timeWindow, yUnits, yMin, yMax, logarithmic, xFormat);
        // Render chart
        renderChart(svgRef.current, scales, axes, innerWidth, innerHeight, width, height, margin);
    }, [
        beg,
        dimensions,
        datasets,
        end,
        timeWindow,
        yMin,
        yMax,
        yUnits,
        xTicks,
        yTicks,
        xMajorTickInterval,
        yMajorTickInterval,
        padding,
        xFormat
    ]);
    function renderChart(svgElement, scales, axes, innerWidth, innerHeight, width, height, margin) {
        const { xScale, yScale } = scales;
        const { xAxis, yAxis, xTickValues, yTickValues } = axes;
        // Select the SVG element
        const svg = d3.select(svgElement);
        svg.selectAll("*").remove();
        // Append group element
        const chart = svg.attr("viewBox", `0 0 ${width} ${height}`).attr("preserveAspectRatio", "xMidYMid meet").attr("data-testid", "line-chart").append("g").attr("transform", `translate(${margin.left},${margin.top})`);
        // Define clip path
        chart.append("defs").append("clipPath").attr("id", clipPathId).append("rect").attr("width", innerWidth).attr("height", innerHeight);
        // Define gradients
        defineGradients(svg, datasets, gradientIds);
        // Draw gridlines
        drawGridlines(chart, xScale, yScale, xTickValues, yTickValues, innerWidth, innerHeight, xMajorTickInterval, yMajorTickInterval);
        // Draw axes
        drawAxes(chart, xAxis, yAxis, innerHeight);
        // Draw lines and areas
        drawLinesAndAreas(chart, datasets, xScale, yScale, innerHeight, clipPathId, gradientIds);
        // Create legend if necessary
        if (datasets.length > 1) {
            const legendData = datasets.map((d)=>({
                    label: d.label || "Data",
                    color: d.color || "blue",
                    classes: d.classes
                }));
            drawLegend(chart, legendData, innerHeight);
        }
        focusLineRef.current = chart.append("line").attr("class", "focus-line").style("stroke", "#000").style("stroke-width", 1).style("opacity", 0);
        if (lastMouseXRef.current != null) {
            focusLineRef.current.style("opacity", 1).attr("x1", lastMouseXRef.current).attr("x2", lastMouseXRef.current).attr("y1", 0).attr("y2", innerHeight);
        }
        renderOverlayWithTooltip(chart, xScale, innerWidth, innerHeight);
    }
    function renderOverlayWithTooltip(chart, xScale, innerWidth, innerHeight) {
        const tooltip = tooltipRef.current;
        const focusLine = focusLineRef.current;
        if (!tooltip) return;
        let lastTooltipHtml = "";
        // add a transparent rect on top to capture mouse events
        overlayRectRef.current = chart.append("rect").attr("class", "overlay-rect").attr("width", innerWidth).attr("height", innerHeight).style("fill", "none").style("pointer-events", "all").on("mouseover", mouseover).on("mousemove", mousemove).on("mouseleave", mouseleave);
        const throttledBuildTooltipHtml = throttle((hoveredTime)=>{
            lastTooltipHtml = buildTooltipHtml(hoveredTime);
        }, 100);
        // Event handlers
        function mouseover() {
            focusLine.style("opacity", 1);
            tooltip.style("opacity", 1);
        }
        function mousemove(event) {
            // convert to local coordinates for crosshair
            const [mouseX] = d3.pointer(event, chart.node());
            lastMouseXRef.current = mouseX;
            focusLine.attr("x1", mouseX).attr("x2", mouseX).attr("y1", 0).attr("y2", innerHeight);
            // build the tooltip HTML
            const hoveredTime = xScale.invert(mouseX);
            throttledBuildTooltipHtml(hoveredTime);
            // Update tooltip content if available
            if (lastTooltipHtml) {
                tooltip.html(lastTooltipHtml);
            }
            // flip tooltip to left side of cursor when up against edge of screen
            const tooltipNode = tooltip.node();
            const tooltipWidth = tooltipNode.offsetWidth;
            const windowWidth = window.innerWidth;
            let left = event.clientX + 15;
            if (event.clientX + 15 + tooltipWidth > windowWidth) {
                left = event.clientX - tooltipWidth - 15;
            }
            // Set the tooltip position
            tooltip.style("left", left + "px").style("top", event.pageY - 10 + "px");
        }
        function mouseleave() {
            focusLine.style("opacity", 0);
            tooltip.style("opacity", 0);
            lastMouseXRef.current = null;
        }
    }
    // Helper that finds the nearest y-value in each dataset at the hoveredTime
    function buildTooltipHtml(hoveredTime) {
        let timeString = "";
        // add date if time window > 24hr
        if (timeWindow > 1440) {
            const month = String(hoveredTime.getMonth() + 1).padStart(2, "0"); // Months are 0-based, add 1 and pad
            const day = String(hoveredTime.getDate()).padStart(2, "0"); // Get day and pad with leading zero if needed
            timeString += `${month}/${day}, `;
        }
        const timeFormat = d3.timeFormat("%H:%M:%S");
        timeString += timeFormat(hoveredTime);
        const colorBlock = (color)=>`
			<span 
				style="
					display:inline-block; 
					width:10px; 
					height:10px; 
					margin-right:4px;
					background-color:${color};
					opacity:0.4;
				"
			></span>
		`;
        let html = `<b>${timeString}</b><br/>`;
        datasets.forEach((dataset)=>{
            // use bisector to find the index of closest x
            const bisectDate = d3.bisector((d)=>d.x).left;
            const idx = bisectDate(dataset.data, hoveredTime);
            // clamp index to valid range
            const iClamped = Math.max(0, Math.min(idx, dataset.data.length - 1));
            const pt = dataset.data[iClamped];
            if (dataset.label) {
                html += colorBlock(dataset.color);
            }
            html += `${dataset.label || "Value"}: <b>${pt.y.toFixed(2)}${yUnits}</b><br/>`;
        });
        return html;
    }
    return /*#__PURE__*/ _jsx("div", {
        ref: containerRef,
        className: containerClass ?? "w-full h-full",
        children: /*#__PURE__*/ _jsx("svg", {
            ref: svgRef,
            className: "w-full h-full"
        })
    });
}
